import { TitleCasePipe } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TextInputModel } from 'projects/money-movement-ui/src/app/shared/components/controls/text-input-control/TextInputModel';
import { thirdPartyCheckFieldsErrorMap } from 'projects/money-movement-ui/src/app/shared/utilities/Constants';
import { Observable, Subject, take } from 'rxjs';
import { SelectControl } from '../../../models/OptionDropdownSelect';
import { CodeAndDescription } from '../../../models/shared/CodeAndDescription';
import { createControl } from '../../../utilities/formUtilities';
import { CheckTransactionDetailsForm } from '../../../models/form/TransactionFormGroup';

@Component({
  standalone: false,
  selector: 'mm-check-third-party-international-fields',
  templateUrl: './check-third-party-international-fields.component.html',
  styleUrls: ['./check-third-party-international-fields.component.scss']
})
export class CheckThirdPartyInternationalFieldsComponent implements OnInit {
  @Input() parentForm!: FormGroup<CheckTransactionDetailsForm>;
  @Input() countryList$: Observable<CodeAndDescription[]>;
  @Input() shouldDisableFieldForEditQcd: boolean = false;
  readonly controlNames = {
    postalCode: 'postalCode',
    stateProvince: 'stateProvince',
    country: 'country',
    city: 'city'
  }
  cityModel: TextInputModel = {
    controlName: this.controlNames.city,
    label: 'City',
    hasGreyText: false,
    htmlMaxLength: 30
  };
  postalCodeModel: TextInputModel = {
    controlName: this.controlNames.postalCode,
    label: 'ZIP/Postal code',
    hasGreyText: false,
    htmlMaxLength: 10
  };
  stateProvinceModel: TextInputModel = {
    controlName: this.controlNames.stateProvince,
    label: 'State/Province/Region',
    hasGreyText: true,
    greyText: '(optional)',
    htmlMaxLength: 30
  };
  countryListDropdown: SelectControl = {
    title: 'Country',
    controlName: this.controlNames.country,
    options: [],
    validationMessage: 'Country is required.',
  };
  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private titleCasePipe: TitleCasePipe) { }

  ngOnInit(): void {
    createControl(this.parentForm, this.controlNames.city, '', [Validators.required], undefined, this.shouldDisableFieldForEditQcd);
    createControl(this.parentForm, this.controlNames.stateProvince, '',undefined, undefined, this.shouldDisableFieldForEditQcd);
    createControl(this.parentForm, this.controlNames.postalCode, '',undefined, undefined, this.shouldDisableFieldForEditQcd);
    createControl(this.parentForm, this.controlNames.country, '', [Validators.required], undefined, this.shouldDisableFieldForEditQcd);

    this.countryList$.pipe(take(1)).subscribe(countryList => {
      this.countryListDropdown.options = countryList.map((state) => {
        return { value: state.code, name: this.titleCasePipe.transform(state.description) };
      });
    });
  }

  ngOnDestroy() {
    if (this.city) this.parentForm.removeControl('city');
    if (this.stateProvince) this.parentForm.removeControl('stateProvince');
    if (this.country) this.parentForm.removeControl('country');
    if (this.postalCode) this.parentForm.removeControl('postalCode');

    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  get city(): FormControl {
    return this.parentForm.get(this.controlNames.city) as FormControl;
  }

  get stateProvince(): FormControl {
    return this.parentForm.get(this.controlNames.stateProvince) as FormControl;
  }

  get postalCode(): FormControl {
    return this.parentForm.get(this.controlNames.postalCode) as FormControl;
  }

  get country(): FormControl {
    return this.parentForm.get(this.controlNames.country) as FormControl;
  }

  get cityError(): string {
    const error = this.mapErrors(this.cityModel.label, this.city);
    return error === undefined ? '' : error;
  }

  get zipCodeError(): string {
    if (this.country.value === 'US') {
      this.postalCodeModel.hasGreyText = false;
      this.postalCodeModel.greyText = '';
      this.postalCode.setValidators([Validators.required]);
      this.postalCode.updateValueAndValidity({ emitEvent: false });
      const error = this.mapErrors(this.postalCodeModel.label, this.postalCode);
      return error === undefined ? '' : error;
    }
    this.postalCodeModel.hasGreyText = true;
    this.postalCodeModel.greyText = '(optional)';
    this.postalCode.setValidators(null);
    this.postalCode.updateValueAndValidity({ emitEvent: false });
    return '';
  }

  private mapErrors(
    name: string,
    control: FormControl | undefined
  ): string | undefined {
    const errorMap = thirdPartyCheckFieldsErrorMap(name);
    const errorKeys = Object.keys(control?.errors ?? {});

    if (errorKeys.length > 0) {
      return errorMap[errorKeys[0] as keyof typeof thirdPartyCheckFieldsErrorMap];
    }

    return '';
  }
}
